import React, { useEffect, useRef, useState } from "react";
import { useCall } from "../contexts/CallContext";
import apiService from "../services/api";
import { useWhatsAppSSE } from "../hooks/useWhatsAppSSE";
import { normalizePhone } from "../utils/phoneUtils";

type PermissionStatus = "accepted" | "rejected" | "expired" | "unknown" | "requested";

const POSITION_STORAGE_KEY = "callWidgetPosition";

const PERMISSION_LABELS: Record<PermissionStatus, string> = {
  accepted: "Patient approved WhatsApp calls",
  requested: "Permission request sent - waiting for the patient's approval",
  rejected: "Patient rejected WhatsApp calls",
  expired: "Call approval expired (older than 7 days)",
  unknown: "No call approval on record",
};

const STATUS_LABELS: Record<string, string> = {
  connecting: "Connecting…",
  ringing: "Ringing…",
  "in-call": "In call",
  ended: "Call ended",
  error: "Call failed",
};

function formatDuration(startedAt: number, now: number): string {
  const totalSeconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Floating WhatsApp-call widget. Mounted once in App.tsx so it survives
 * screen navigation while a call is active.
 */
const CallWidget: React.FC = () => {
  const { activeCall, isMuted, hangUp, toggleMute, dismissCall, requestCallPermission, startWhatsAppCall } = useCall();
  const [now, setNow] = useState(() => Date.now());
  const [permissionState, setPermissionState] = useState<"idle" | "sending" | "sent" | "already_sent" | "failed">("idle");
  const [permissionStatus, setPermissionStatus] = useState<{
    status: PermissionStatus;
    valid_until: string | null;
    phone?: string; // the number actually dialed (get_phone resolution)
  } | null>(null);
  const autoCalledRef = useRef(false);
  // null = default bottom-right docking; set once the user drags the widget.
  const [position, setPosition] = useState<{ x: number; y: number } | null>(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      if (!saved) return null;
      const pos = JSON.parse(saved);
      if (typeof pos?.x !== "number" || typeof pos?.y !== "number") return null;
      // Clamp to the current viewport in case the screen shrank since last time.
      return {
        x: Math.min(Math.max(0, pos.x), window.innerWidth - 320),
        y: Math.min(Math.max(0, pos.y), window.innerHeight - 100),
      };
    } catch {
      return null;
    }
  });
  const widgetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const isPostCall = activeCall?.status === "error" || activeCall?.status === "ended";
  const postCallPatientId = isPostCall ? activeCall.patientId : null;
  const postCallPhone = isPostCall ? activeCall.phone : "";

  useEffect(() => {
    // After a call ends/fails, show the latest known permission state for this patient.
    if (postCallPatientId == null) {
      setPermissionStatus(null);
      return;
    }
    let stale = false;
    // Small delay: the Twilio status callback self-heals the stored permission
    // from the call outcome, and we want to read the post-call state.
    const timer = setTimeout(() => {
      apiService
        .getWhatsAppCallPermissionStatus({
          token: localStorage.getItem("token") || "",
          patient_id: Number(postCallPatientId),
          phone: postCallPhone || undefined,
        })
        .then(res => {
          if (stale) return;
          setPermissionStatus({ status: res.status, valid_until: res.valid_until, phone: res.phone });
          if (res.status === "requested") {
            // A request is already out (possibly from a previous session) - show the
            // "waiting for reply" state and start watching for the patient's answer.
            setPermissionState(prev => (prev === "idle" ? "already_sent" : prev));
          }
        })
        .catch(() => {
          // Can't determine the state - fall back to offering the request button.
          if (!stale) setPermissionStatus({ status: "unknown", valid_until: null });
        });
    }, 1500);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [postCallPatientId, postCallPhone]);

  useEffect(() => {
    if (activeCall?.status !== "in-call") return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [activeCall?.status]);

  useEffect(() => {
    // Reset the permission button whenever a new call starts.
    if (activeCall?.status === "connecting") {
      setPermissionState("idle");
    }
  }, [activeCall?.status]);

  // Live approval push: while the widget shows a finished call, listen on the SSE
  // channel of the number that was dialed - the inbound webhook publishes the
  // patient's permission reply there the moment it arrives.
  useWhatsAppSSE(
    isPostCall && permissionStatus?.phone ? normalizePhone(permissionStatus.phone) : undefined,
    true,
    {
      onCallPermission: (status) =>
        setPermissionStatus(prev => ({ status, valid_until: null, phone: prev?.phone })),
    }
  );

  useEffect(() => {
    // The therapist asked for permission and the patient just approved: if the
    // widget is still open, place the call automatically.
    if (
      isPostCall &&
      (permissionState === "sent" || permissionState === "already_sent") &&
      permissionStatus?.status === "accepted" &&
      !autoCalledRef.current &&
      activeCall
    ) {
      autoCalledRef.current = true;
      startWhatsAppCall({
        phone: activeCall.phone,
        patientId: activeCall.patientId,
        patientName: activeCall.patientName,
        meetingId: activeCall.meetingId,
      });
    }
  }, [isPostCall, permissionState, permissionStatus, activeCall, startWhatsAppCall]);

  useEffect(() => {
    // After sending a permission request, watch for the patient's reply (captured
    // by the inbound webhook) so the widget flips to approved/rejected live.
    if ((permissionState !== "sent" && permissionState !== "already_sent") || postCallPatientId == null) return;
    let stale = false;
    const interval = setInterval(async () => {
      try {
        const res = await apiService.getWhatsAppCallPermissionStatus({
          token: localStorage.getItem("token") || "",
          patient_id: Number(postCallPatientId),
          phone: postCallPhone || undefined,
        });
        if (stale) return;
        if (res.status === "accepted" || res.status === "rejected") {
          setPermissionStatus({ status: res.status, valid_until: res.valid_until, phone: res.phone });
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    const stopTimer = setTimeout(() => clearInterval(interval), 180000); // give up after 3 min
    return () => {
      stale = true;
      clearInterval(interval);
      clearTimeout(stopTimer);
    };
  }, [permissionState, postCallPatientId, postCallPhone]);

  if (!activeCall) return null;

  const isLive = activeCall.status === "connecting" || activeCall.status === "ringing" || activeCall.status === "in-call";

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    const rect = widgetRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  const onHeaderPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    const el = widgetRef.current;
    if (!drag || !el) return;
    const x = drag.origX + (e.clientX - drag.startX);
    const y = drag.origY + (e.clientY - drag.startY);
    setPosition({
      x: Math.min(Math.max(0, x), window.innerWidth - el.offsetWidth),
      y: Math.min(Math.max(0, y), window.innerHeight - el.offsetHeight),
    });
  };

  const onHeaderPointerUp = () => {
    if (dragRef.current && position) {
      try {
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
      } catch { /* storage may be unavailable - position just won't persist */ }
    }
    dragRef.current = null;
  };

  const handleRequestPermission = async () => {
    autoCalledRef.current = false; // arm auto-call for this request
    setPermissionState("sending");
    try {
      const res = await requestCallPermission(activeCall.patientId, activeCall.phone);
      if (res?.already_approved) {
        // Permanent permission already exists - show approved (no expiry).
        setPermissionStatus(prev => ({ status: "accepted", valid_until: null, phone: prev?.phone }));
        setPermissionState("sent");
      } else if (res?.already_sent) {
        // Meta rate limit (63005): a request is already pending - keep watching
        // for the patient's reply instead of showing a failure.
        setPermissionState("already_sent");
      } else if (res?.error) {
        console.error("[CallWidget] Permission request not delivered:", res.error);
        setPermissionState("failed");
      } else {
        setPermissionState("sent");
      }
    } catch (error) {
      console.error("[CallWidget] Permission request failed", error);
      setPermissionState("failed");
    }
  };

  return (
    <div
      ref={widgetRef}
      style={{
        position: "fixed",
        zIndex: 2000,
        width: 320,
        ...(position ? { left: position.x, top: position.y } : { bottom: 24, right: 24 }),
      }}
      className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        className="bg-green-600 px-4 py-2 flex items-center justify-between cursor-move select-none touch-none"
        title="Drag to move"
      >
        <span className="text-white text-sm font-semibold">WhatsApp Call</span>
        {!isLive && (
          <button
            onClick={dismissCall}
            onPointerDown={(e) => e.stopPropagation()}
            title="Close"
            className="text-white hover:text-green-100 text-lg leading-none"
          >
            ×
          </button>
        )}
      </div>
      <div className="px-4 py-3">
        <p className="text-sm font-bold text-gray-900 truncate">{activeCall.patientName}</p>
        <p className="text-xs text-gray-500">{activeCall.phone}</p>
        <p className="text-sm mt-1 text-gray-700">
          {STATUS_LABELS[activeCall.status] || activeCall.status}
          {activeCall.status === "in-call" && activeCall.startedAt && (
            <span className="ml-2 font-mono text-green-700">{formatDuration(activeCall.startedAt, now)}</span>
          )}
        </p>
        {(activeCall.status === "error" || activeCall.status === "ended") && (
          <div className="mt-1">
            {activeCall.error && <p className="text-xs text-red-600">{activeCall.error}</p>}
            {permissionStatus && (
              <p
                className={`text-xs mt-1 font-medium ${
                  permissionStatus.status === "accepted" ? "text-green-700" : "text-amber-700"
                }`}
              >
                {PERMISSION_LABELS[permissionStatus.status]}
                {permissionStatus.status === "accepted" && permissionStatus.valid_until && (
                  <span className="font-normal text-gray-500">
                    {" "}(until {new Date(permissionStatus.valid_until).toLocaleDateString()})
                  </span>
                )}
              </p>
            )}
            {permissionStatus && permissionStatus.status !== "accepted" && (
              <>
                <p className="text-xs text-gray-500 mt-1">
                  WhatsApp calls need the patient's approval. If the call didn't reach the patient, they may
                  not have approved calls yet:
                </p>
                <button
                  onClick={handleRequestPermission}
                  disabled={permissionState === "sending" || permissionState === "sent" || permissionState === "already_sent"}
                  className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                >
                  {permissionState === "sending"
                    ? "Sending…"
                    : permissionState === "sent"
                    ? "Permission request sent"
                    : permissionState === "already_sent"
                    ? "Request already sent - waiting for the patient's reply"
                    : permissionState === "failed"
                    ? "Failed - try again"
                    : "Send call permission request"}
                </button>
              </>
            )}
          </div>
        )}
        <div className="flex items-center justify-center gap-3 mt-3">
          {isLive && (
            <>
              <button
                data-ai-id="call-toggle-mute"
                onClick={toggleMute}
                title={isMuted ? "Unmute" : "Mute"}
                className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${
                  isMuted ? "bg-yellow-100 border-yellow-400 text-yellow-700" : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {isMuted ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9l4 6m0-6l-4 6" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-14 0m7 7v3m-3 0h6M12 4a3 3 0 00-3 3v4a3 3 0 006 0V7a3 3 0 00-3-3z" />
                  </svg>
                )}
              </button>
              <button
                onClick={hangUp}
                title="Hang up"
                className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" style={{ transform: "rotate(135deg)" }}>
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                </svg>
              </button>
            </>
          )}
          {!isLive && (
            <>
              <button
                onClick={() =>
                  startWhatsAppCall({
                    phone: activeCall.phone,
                    patientId: activeCall.patientId,
                    patientName: activeCall.patientName,
                    meetingId: activeCall.meetingId,
                  })
                }
                className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700"
              >
                Call again
              </button>
              <button
                onClick={dismissCall}
                className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallWidget;
